import _typeof from "@babel/runtime/helpers/typeof";
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import { ACTION } from '@atlaskit/editor-common/analytics';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { DiscardScreen } from '@atlassian/generative-ai-modal/screens/Discard';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlowEventsQueue } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
export var getDiscardMessage = function getDiscardMessage(_ref) {
  var formatMessage = _ref.formatMessage,
    experienceState = _ref.experienceState;
  var discardPrompt = formatMessage(aiExperienceApplicationMessages.discardMessagePrompt);
  var discardResponse = formatMessage(aiExperienceApplicationMessages.discardMessageResponse);
  if (_typeof(experienceState) === 'object' && 'user input state' in experienceState) {
    return discardPrompt;
  }
  if (experienceState === 'review state' || _typeof(experienceState) === 'object' && 'loading state' in experienceState) {
    return discardResponse;
  }
  return discardResponse;
};
export function DiscardScreenWithLogic(_ref2) {
  var latestPrompt = _ref2.latestPrompt,
    state = _ref2.state,
    editCount = _ref2.editCount,
    retryPromptCount = _ref2.retryPromptCount,
    history = _ref2.history,
    onceDiscard = _ref2.onceDiscard,
    channelId = _ref2.channelId;
  var aiExperienceCommonData = useAIExperienceCommonDataContext();
  var endExperience = aiExperienceCommonData.endExperience,
    sendToAIExperienceMachine = aiExperienceCommonData.sendToAIExperienceMachine,
    configItem = aiExperienceCommonData.configItem,
    includeKnowledgeFromCurrentPage = aiExperienceCommonData.includeKnowledgeFromCurrentPage;
  var inputOutputDiffRatio = useMemo(function () {
    var lastHistoryEvent = history.entries[history.positionFromEnd];
    return history.entries.length === 0 ? '' : lastHistoryEvent.inputOutputDiffRatio;
  }, [history]);
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
  var analyticsFlow = useAnalyticsFlowEventsQueue({
    stepName: 'discard',
    attributes: {
      editCount: editCount,
      retryPromptCount: retryPromptCount,
      inputOutputDiffRatio: inputOutputDiffRatio,
      includeKnowledgeFromCurrentPage: includeKnowledgeFromCurrentPage
    }
  });
  return /*#__PURE__*/React.createElement(DiscardScreen, {
    text: getDiscardMessage({
      formatMessage: formatMessage,
      experienceState: state
    }),
    onCancel: function onCancel() {
      sendToAIExperienceMachine({
        type: 'disable discard'
      });
    },
    onDiscard: function onDiscard() {
      /**
       * Introduced in EDF-1443 to track discard event confirmations
       * separately to normal discard events which get tracked
       * earlier in this file
       */
      fireAIAnalyticsEvent({
        payload: createUnifiedAnalyticsPayload({
          action: ACTION.DISMISSED,
          interactionID: channelId !== null && channelId !== void 0 ? channelId : analyticsFlow.getLastAiInteractionId(),
          experienceName: convertExperienceName(configItem.key.toString()),
          isProactive: false,
          attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({
            init: false
          }),
          invokedFrom: aiExperienceCommonData.lastTriggeredFrom
        })
      });
      if (onceDiscard) {
        sendToAIExperienceMachine({
          type: 'disable discard'
        });
        onceDiscard();
      } else {
        endExperience();
      }
    }
  });
}